import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Lock } from "lucide-react";
import { TextField } from "@/shared/ui/core";
import { suggestSlug, type PackageFormValues } from "../packageForm";

export interface PackageSlugFieldProps {
  /** `true` al editar: el slug ya está publicado y no se toca. */
  locked: boolean;
}

/**
 * La parte legible de la URL del paquete.
 *
 * ── Al crear: se sugiere solo ────────────────────────────────────────
 * El slug se va escribiendo a partir del nombre en español mientras el
 * campo no se haya tocado a mano. En cuanto alguien escribe en él,
 * react-hook-form lo marca como "sucio" y la sugerencia se calla para
 * siempre: nada se sobrescribe por debajo mientras se teclea.
 *
 * (`setValue` con `shouldDirty: false` —el valor por omisión— es lo que
 * hace que rellenarlo automáticamente NO cuente como haberlo tocado.)
 *
 * ── Al editar: bloqueado ─────────────────────────────────────────────
 * Aquí pesa incluso más que en un producto. Un paquete se contrata como
 * SUSCRIPCIÓN, y su enlace circula en el material con el que se vende;
 * cambiar el slug manda a un 404 a cada persona que llegue por él, y
 * quien ya lo tiene contratado ni se entera hasta que va a enseñárselo a
 * alguien. Por eso el contrato lo omite de `packageUpdateSchema` y aquí
 * el campo va deshabilitado, con el motivo escrito al lado en vez de
 * solo gris.
 */
export function PackageSlugField({ locked }: PackageSlugFieldProps) {
  const { t } = useTranslation();
  const { control, setValue, formState } = useFormContext<PackageFormValues>();

  const name = useWatch({ control, name: "translations.es.name" });
  const touchedByHand = Boolean(formState.dirtyFields.slug);

  useEffect(() => {
    if (locked || touchedByHand) return;
    setValue("slug", suggestSlug(name ?? ""));
  }, [locked, touchedByHand, name, setValue]);

  return (
    <TextField
      name="slug"
      label={t("Slug")}
      required={!locked}
      disabled={locked || undefined}
      icon={locked ? <Lock /> : undefined}
      placeholder="paquete-bienestar"
      hint={
        locked
          ? t("El slug no se cambia: rompería los enlaces ya compartidos.")
          : t("Se sugiere desde el nombre. Solo minúsculas, números y guiones.")
      }
      fieldClassName="sm:col-span-2"
      className="font-mono"
    />
  );
}
